import {
  IOtpEnvelopeNullableContent,
  IOtpRequestBody,
  IOtpSelectUserBody,
  IOtpVerifyBody,
  IOtpVerifySuccessResponse,
} from '@aca-new/app/pages/login/shared/models/interfaces/login-otp-auth.interface';
import { IOtpSelectUserHttpEnvelope, mapOtpSelectUserHttpResponseToLoginTokenResponse } from '@aca-new/app/pages/login/shared/models/interfaces/login-otp-auth.mapper';
import { ILoginTokenResponse } from '@aca-new/app/pages/login/shared/models/interfaces/token-response.interface';
import { IBackOfficeLoginData, ILoginCredential, IPasswordResetInfo, IUser } from '@aca-new/app/pages/user/shared/models/interfaces/user.interface';
import { AI_API_ACCESS_TOKEN } from '@aca-new/app/shared/constants/ai-api-access-token.constants';
import { SERVER_URL } from '@aca-new/app/shared/constants/app.constants';
import { EStorageKeys } from '@aca-new/app/shared/models/enums/storage-keys.enum';
import { IAuthenticationData } from '@aca-new/app/shared/models/interfaces/authentication-data.interface';
import { HttpResponseBodyNullableType } from '@aca-new/app/shared/models/interfaces/http-response-body.interface';
import { SkipInterceptorService } from '@aca-new/app/shared/services/http-services/http.service';
import { StorageService } from '@aca-new/app/shared/services/storage-services/storage.service';
import { AppAuthenticationService } from '@aca-new/app/shared/services/user-services/app-authentication/app-authentication.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public constructor(
    private readonly _httpClient: HttpClient,
    private readonly _skipInterceptorService: SkipInterceptorService,
    private readonly _storageService: StorageService,
    private readonly _appAuthenticationService: AppAuthenticationService
  ) {}

  public getUserById$(userId: string, isRefresh?: boolean): Observable<HttpResponseBodyNullableType<IUser>> {
    let url = `${SERVER_URL}/user/v2/${userId}`;

    if (isRefresh) {
      url += '?refresh=true';
    }

    return this._httpClient
      .get<HttpResponseBodyNullableType<IUser>>(url, { observe: 'body' })
      .pipe(map((response: Readonly<HttpResponseBodyNullableType<IUser>>): HttpResponseBodyNullableType<IUser> => response));
  }

  public getToken$(data: ILoginCredential): Observable<ILoginTokenResponse> {
    return this._skipInterceptorService.httpClient.post<ILoginTokenResponse>(`${SERVER_URL}/auth/v2/token`, data, {
      observe: 'body',
      headers: this._aiApiAccessHeaders(),
    });
  }

  public requestOtpSignInCode$(body: IOtpRequestBody): Observable<IOtpEnvelopeNullableContent> {
    return this._skipInterceptorService.httpClient.post<IOtpEnvelopeNullableContent>(`${SERVER_URL}/auth/v2/otp/request`, body, {
      observe: 'body',
      headers: this._aiApiAccessHeaders(),
    });
  }

  public verifyOtpSignInCode$(body: IOtpVerifyBody): Observable<IOtpVerifySuccessResponse> {
    return this._skipInterceptorService.httpClient.post<IOtpVerifySuccessResponse>(`${SERVER_URL}/auth/v2/otp/verify`, body, {
      observe: 'body',
      headers: this._aiApiAccessHeaders(),
    });
  }

  public selectOtpUser$(body: IOtpSelectUserBody): Observable<ILoginTokenResponse> {
    return this._skipInterceptorService.httpClient
      .post<IOtpSelectUserHttpEnvelope>(`${SERVER_URL}/auth/v2/otp/select-user`, body, {
        observe: 'body',
        headers: this._aiApiAccessHeaders(),
      })
      .pipe(map((resp: Readonly<IOtpSelectUserHttpEnvelope>): ILoginTokenResponse => mapOtpSelectUserHttpResponseToLoginTokenResponse(resp)));
  }

  public getTokenFromBackOffice$(data: IBackOfficeLoginData): Observable<ILoginTokenResponse> {
    const url = `${SERVER_URL}/auth/v2/login-by-backoffice-token/user/${data.userId}/token/${data.token}/${data.cacheType}`;

    return this._skipInterceptorService.httpClient.post<ILoginTokenResponse>(
      url,
      {},
      {
        observe: 'body',
        headers: this._aiApiAccessHeaders(),
      }
    );
  }

  public saveAuthenticationData(params: Params): void {
    const { token, userId, userName, sessionId, loginType, refreshKey, userType, validBefore } = params;

    if (token && userId && sessionId) {
      const authData: IAuthenticationData = {
        token: decodeURIComponent(token as string).trim(),
        sessionId: decodeURIComponent(sessionId as string).trim(),
        loginType: decodeURIComponent((loginType ?? '') as string).trim(),
        refreshKey: decodeURIComponent((refreshKey ?? '') as string).trim(),
        userType: decodeURIComponent((userType ?? '') as string).trim(),
        validBefore: decodeURIComponent((validBefore ?? '') as string).trim(),
        userId: decodeURIComponent((userId ?? '') as string).trim(),
        userName: decodeURIComponent((userName ?? '') as string).trim(),
      };

      this._appAuthenticationService.saveAuthenticationData(authData);
    }
  }

  public updateUserDataSession(userData: IUser): void {
    this._appAuthenticationService.updateUserData(userData);
    this._storageService.setItem(EStorageKeys.USER_DATA, JSON.stringify(userData));
  }

  public resetPassword$(email: string): Observable<HttpResponseBodyNullableType<undefined>> {
    const url = `${SERVER_URL}/user/v3/reset-password-by-email`;

    return this._skipInterceptorService.httpClient.put<HttpResponseBodyNullableType<undefined>>(url, { email }, { observe: 'body' });
  }

  public validateResetToken$(token: string): Observable<HttpResponseBodyNullableType<IPasswordResetInfo>> {
    const url = `${SERVER_URL}/user/v2/password-reset-info?token=${token}`;

    return this._skipInterceptorService.httpClient.get<HttpResponseBodyNullableType<IPasswordResetInfo>>(url, { observe: 'body' });
  }

  public submitNewPassword$(userId: string, token: string, password: string): Observable<HttpResponseBodyNullableType<undefined>> {
    const url = `${SERVER_URL}/user/v3/${userId}/password-by-token`;

    return this._skipInterceptorService.httpClient.put<HttpResponseBodyNullableType<undefined>>(url, { token, password }, { observe: 'body' });
  }

  public watchUserDataRefresh$(): Observable<HttpResponseBodyNullableType<IUser>> {
    const userId = this._storageService.getItem(EStorageKeys.USER_ID);

    return this.getUserById$(userId, true).pipe(
      tap({
        next: (userData: HttpResponseBodyNullableType<IUser>): void => {
          if (userData?.content) {
            this.updateUserDataSession(userData.content);
          }
        },
      })
    );
  }

  private _aiApiAccessHeaders(): HttpHeaders {
    return new HttpHeaders().set('ai-api-access-token', AI_API_ACCESS_TOKEN);
  }
}
